/**
 * The production summary & pack list (Matt, Oct 20: "Pages 2–3. Replaces the
 * pull list"): the kitchen's item-by-stop matrix, one row per thing to make,
 * one column per stop in route order, a Total and a Done box to tick when the
 * row is packed into its stop carts. It is its own section, after the
 * shopping list:
 *
 * - "Milk" first, its own table (PROD-1502: "Show milk separately"; Abner,
 *   Oct 19: "MILK should be displayed SEPARATELY as its own table
 *   (everywhere!)");
 * - "Production": today's meals in mealtime order, each with its entrées and
 *   their "= Lunch entrées" subtotal (the true meal count, never a sum of
 *   rows), then the sides and extras; a later day's meal last, "PACK
 *   SEPARATELY";
 * - a bulk row's stop cell counts containers from the packing plans (Matt's
 *   design: "17 pans" over "325 sv"), every container full and the servings
 *   left over as "+ 5 EA" (D54): "16 pans", "+ 5 EA" over "325 sv"; a cell
 *   the plans do not cover counts servings (D23: never a wrong count), a bulk
 *   one with the design's "sv", "325 sv", so it never passes for a count of
 *   pans;
 * - "Stop staged: every row for that stop is in its cart", a box per stop.
 *
 * Rows merge across stops on the same key as the Orders "All schools" card
 * (`mergeCampusesMealtimes`): name, serving, unit, temperature and bulk mode.
 * A day with more than `STOPS_PER_MATRIX` stops prints in column sets ("STOPS
 * 1–12", "STOPS 13–16"), a section each, each with the all-stop Total. Pure;
 * unit-tested.
 */

import { containerNameFor, formatExtraServings, mergeBulkPackingPlans } from '@ordo/constants/packing-plan'

import { deliverableMode } from '@/lib/chefs/deliverable-copy'
import { hotterTemperature } from '@/lib/chefs/pack-groups'
import { packingCountParts } from '@/lib/chefs/packing'
import { summaryPackageLines } from '@/lib/chefs/print-packages'
import type { Deliverable, Mealtime, Temperature } from '@/lib/chefs/types'
import { MEALTIME_LABEL } from '@/lib/chefs/types'

import { KITCHEN_COPY, type PacketDay } from './day'
import {
  campusMealSections,
  compareMealSections,
  isEntree,
  type MealSection,
  mealtimeCaps,
  type ServeWords,
} from './meals'
import { deliverTemperature, dietTags, portionDetail } from './rows'
import type { PacketStop } from './stops'
import { type CellCount, type PacketSection, type PacketUnit } from './units'

/** Stop columns one page holds beside the item, its temperature, the Total and Done. */
export const STOPS_PER_MATRIX = 12

interface MatrixRow {
  key: string
  name: string
  detail: string | null
  temperature: Temperature | null
  diets: string[]
  counts: Array<number | null>
  total: number
  /** The deliverables behind each stop's cell, for its containers and whether it is bulk. */
  perStop: Deliverable[][]
}

/** A group of the matrix across every stop: its band, its rows and (entrées only) its subtotal. */
interface MatrixGroup {
  key: string
  text: string
  mealKey: string
  mealTitle: string
  rows: MatrixRow[]
  subtotal: { label: string; counts: Array<number | null>; total: number } | null
}

/** One meal across the day's stops: the stop's own section for it, or null where the stop does not take it. */
export interface MealAcrossStops {
  key: string
  mealtime: Mealtime
  serve: ServeWords
  perStop: Array<MealSection | null>
}

/** The day's meals in mealtime order, each with every stop's section for it. */
export function mealsAcrossStops(day: Pick<PacketDay, 'stops' | 'iso'>): MealAcrossStops[] {
  const meals = new Map<string, MealAcrossStops>()
  day.stops.forEach((stop, i) => {
    for (const section of campusMealSections(stop.campus, day.iso)) {
      let meal = meals.get(section.key)
      if (!meal) {
        meal = {
          key: section.key,
          mealtime: section.mealtime,
          serve: section.serve,
          perStop: day.stops.map(() => null),
        }
        meals.set(section.key, meal)
      }
      meal.perStop[i] = section
    }
  })
  return [...meals.values()].sort(compareMealSections)
}

/** Today's meals first, in their order, then a later day's: the order every page lists the meals in. */
export function todayThenLater<M extends { serve: ServeWords }>(meals: readonly M[]): M[] {
  return [...meals.filter((meal) => !meal.serve.later), ...meals.filter((meal) => meal.serve.later)]
}

/** A meal's servings across the day's stops: its true meal count, never a sum of rows. */
export function mealTotal(meal: MealAcrossStops): number {
  return meal.perStop.reduce((sum, section) => sum + (section?.block.meals ?? 0), 0)
}

/** The merge key the Orders "All schools" card uses (`mergeCampusesMealtimes`). */
export function deliverableMergeKey(d: Deliverable): string {
  return `${d.name}|${portionDetail(d) ?? ''}|${d.unit}|${deliverTemperature(d)}|${d.bulk ? 'bulk' : 'plated'}`
}

function addTo(
  rows: Map<string, MatrixRow>,
  key: string,
  stopIndex: number,
  stopCount: number,
  qty: number,
  seed: Omit<MatrixRow, 'key' | 'counts' | 'total' | 'perStop'>,
  deliverable: Deliverable | null,
) {
  let row = rows.get(key)
  if (!row) {
    row = {
      key,
      ...seed,
      counts: Array.from({ length: stopCount }, () => null),
      total: 0,
      perStop: Array.from({ length: stopCount }, () => []),
    }
    rows.set(key, row)
  }
  row.counts[stopIndex] = (row.counts[stopIndex] ?? 0) + qty
  row.total += qty
  row.temperature = hotterTemperature(row.temperature, seed.temperature)
  if (deliverable) {
    row.perStop[stopIndex]!.push(deliverable)
  }
}

function deliverableSeed(d: Deliverable): Omit<MatrixRow, 'key' | 'counts' | 'total' | 'perStop'> {
  // The kitchen's temperature: CHILLED for food delivered cold (the Oct 21 canvas's temp rule).
  return { name: d.name, detail: portionDetail(d), temperature: deliverTemperature(d), diets: dietTags(d.diets) }
}

/** A meal's rows across the stops, merged; the biggest first. */
function mealRows(meal: MealAcrossStops, keep: (d: Deliverable) => boolean): MatrixRow[] {
  const rows = new Map<string, MatrixRow>()
  meal.perStop.forEach((section, i) => {
    for (const d of section?.block.deliverables ?? []) {
      if (keep(d)) {
        addTo(rows, deliverableMergeKey(d), i, meal.perStop.length, d.qty, deliverableSeed(d), d)
      }
    }
  })
  return [...rows.values()].sort((a, b) => b.total - a.total || a.name.localeCompare(b.name))
}

/**
 * A stop's containers for one production row, from its deliverables' packing
 * plans pooled (`mergeBulkPackingPlans`): "16" "pans", then "+ 5" "EA" (D54).
 * Null, so the cell counts servings, unless the plans cover every serving of
 * the cell (D23: never a partial count). A plan may hold more than the cell:
 * the servings of a row packed in its containers ("Packed with Corn").
 */
export function cellContainers(deliverables: readonly Deliverable[]): CellCount[] | null {
  if (deliverables.length === 0 || deliverables.some((d) => d.packingIncomplete)) {
    return null
  }
  const plans = deliverables.flatMap((d) => d.packing ?? [])
  const planned = plans.reduce((sum, plan) => sum + plan.totalServings, 0)
  const servings = deliverables.reduce((sum, d) => sum + d.qty, 0)
  if (plans.length === 0 || planned + 1e-6 < servings) {
    return null
  }
  const { containers, extra } = packingCountParts(mergeBulkPackingPlans(plans))
  return containers.length > 0 ? [...containers, ...(extra ? [extra] : [])] : null
}

/**
 * A stop's cell holds food that rides in shared vessels (pans, boxes), as the
 * stop's BULK type reads it (`deliverableMode`). Where the plans do not cover
 * it the cell still says its count is servings, "325 sv": a bare "325" at a
 * BULK stop, under "counts are containers", reads as 325 pans.
 */
export function isBulkCell(deliverables: readonly Deliverable[]): boolean {
  return deliverables.some((d) => deliverableMode(d) === 'bulk')
}

/** "LUNCH · SERVE TODAY", "BREAKFAST · SERVE MON 10/19 · PACK SEPARATELY". */
export function mealBandText(meal: Pick<MealAcrossStops, 'mealtime' | 'serve'>): string {
  const base = `${mealtimeCaps(meal.mealtime)} · ${meal.serve.label}`
  return meal.serve.later ? `${base} · PACK SEPARATELY` : base
}

/**
 * The production table's groups: today's meals, then a later day's (Matt, Oct
 * 20: "Wed breakfast last (pack separately)"); a meal with entrées and other
 * rows splits under its true meal count.
 */
function productionGroups(day: PacketDay): MatrixGroup[] {
  const groups: MatrixGroup[] = []
  for (const meal of todayThenLater(mealsAcrossStops(day))) {
    const entrees = mealRows(meal, isEntree)
    const others = mealRows(meal, (d) => !isEntree(d))
    const mealTitle = `${MEALTIME_LABEL[meal.mealtime].replace('Snack', 'snack')}${
      meal.serve.later
        ? ` · ${meal.serve.label
            .toLowerCase()
            .replace(/^serve/, 'Serve')
            .replace(
              /\b(mon|tue|wed|thu|fri|sat|sun)\b/g,
              (day) => day.charAt(0).toUpperCase() + day.slice(1),
            )} · Pack separately`
        : ''
    }`
    // One table per meal (the Oct 21 mockup): entrées first, then the rest, with no MEALS / SIDES split.
    if (entrees.length + others.length > 0) {
      groups.push({
        key: meal.key,
        mealKey: meal.key,
        mealTitle,
        text: 'MEALS',
        rows: [...entrees, ...others],
        subtotal: null,
      })
    }
  }
  return groups
}

/** The day's stops in column sets that fit a page: [0, 12), [12, 24)… */
export function stopColumnSets(stopCount: number, perSet: number = STOPS_PER_MATRIX): Array<[number, number]> {
  const sets: Array<[number, number]> = []
  for (let from = 0; from < stopCount; from += perSet) {
    sets.push([from, Math.min(stopCount, from + perSet)])
  }
  return sets.length > 0 ? sets : [[0, 0]]
}

/** "Stops 1–12", "Stop 13". */
function stopSpan(stops: readonly PacketStop[]): string {
  const first = stops[0]!.identity.number
  const last = stops[stops.length - 1]!.identity.number
  return first === last ? `Stop ${first}` : `Stops ${first}–${last}`
}

/** The rows that have something at the set's stops; every row keeps its all-stop Total. */
function rowsAtStops(rows: readonly MatrixRow[], [from, to]: [number, number]): MatrixRow[] {
  return rows.filter((row) => row.counts.slice(from, to).some((count) => count !== null))
}

function preparedYield(deliverables: readonly Deliverable[]): string[] {
  const quantities = new Map<string, { name: string; unit: string; quantity: number }>()
  if (deliverables.some((d) => d.components.length === 0 || d.components.some((c) => !c.portion))) {
    return []
  }
  for (const d of deliverables) {
    for (const c of d.components) {
      const portion = c.portion!
      const key = `${c.name}|${portion.unit}`
      const entry = quantities.get(key) ?? { name: c.name, unit: portion.unit, quantity: 0 }
      entry.quantity += d.qty * portion.quantity
      quantities.set(key, entry)
    }
  }
  return [...quantities.values()].map(
    (entry) =>
      `${quantities.size > 1 ? entry.name + ': ' : ''}${entry.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })} ${entry.unit}`,
  )
}

export function packingInstructions(deliverables: readonly Deliverable[]): string[] {
  if (!cellContainers(deliverables)) {
    // Keep known individual packages and shared-packing references. An
    // unresolved bulk plan may name its vessel, but cannot claim a count.
    const groups = new Map<string, Deliverable>()
    for (const d of deliverables) {
      // Sections are in the key: a bag of one orange cup and a bag of two are different packs.
      const key = JSON.stringify([deliverableMode(d), d.packaging, d.packagingSkuKind, d.packedWith, d.sections])
      const previous = groups.get(key)
      groups.set(key, { ...d, packing: undefined, qty: (previous?.qty ?? 0) + d.qty })
    }
    return [
      ...new Set(
        [...groups.values()].flatMap((d) =>
          summaryPackageLines(d).map(
            (line) => `${line.section ? '› ' : ''}${line.count ? line.count.replace('x', '×') + ' ' : ''}${line.name}`,
          ),
        ),
      ),
    ]
  }
  const portions = deliverables.flatMap((d) =>
    d.components.length === 1 && d.components[0]?.portion ? [d.components[0].portion] : [],
  )
  const portion =
    portions.length === deliverables.length &&
    portions.every((p) => p.quantity === portions[0]!.quantity && p.unit === portions[0]!.unit)
      ? portions[0]
      : null
  const amount = (servings: number) =>
    portion
      ? `${(servings * portion.quantity).toLocaleString(undefined, { maximumFractionDigits: 2 })} ${portion.unit.replace(/^cups?$/i, 'C')}`
      : `${servings.toLocaleString()} servings`
  const lines: string[] = []
  for (const plan of mergeBulkPackingPlans(deliverables.flatMap((d) => d.packing ?? []))) {
    const fills = new Map<number, number>()
    for (const servings of plan.containers) {
      fills.set(servings, (fills.get(servings) ?? 0) + 1)
    }
    for (const [servings, count] of fills) {
      lines.push(`${count}× ${containerNameFor(count, plan.container.name)} · ${amount(servings)} each`)
    }
    // Whole containers, then what is left in the food's own unit ("+ 3 hamburgers", "+ 2.5 C"); never another
    // container rounded up for it (Sheila, Oct 22).
    if (plan.extra > 0) {
      lines.push(plan.extraMeasure ? formatExtraServings(plan.extra, plan.extraMeasure) : `+ ${amount(plan.extra)}`)
    }
  }
  return lines
}

function matrixRowUnit(
  group: string,
  row: MatrixRow,
  [from, to]: [number, number],
  numbers: readonly string[],
): PacketUnit {
  return {
    id: `${group}|${row.key}`,
    group,
    role: 'row',
    kind: 'matrix-row',
    stops: stopRange(numbers.filter((_, i) => row.counts[i] !== null)),
    name: row.name,
    detail: row.detail,
    preparedYield: preparedYield(row.perStop.flat()),
    expectedContainers: cellContainers(row.perStop.flat()),
    packInstructions: packingInstructions(row.perStop.flat()),
    temperature: row.temperature,
    diets: row.diets,
    tags: [],
    counts: row.counts.slice(from, to),
    total: row.total,
    containers: row.perStop.slice(from, to).map((deliverables) => cellContainers(deliverables)),
    bulk: row.perStop.slice(from, to).map((deliverables) => isBulkCell(deliverables)),
  }
}

/**
 * One column set's groups: each band, its rows and its subtotal. A row with
 * nothing at the set's stops is left off; every row keeps its all-stop Total.
 * Bands after the first sit lower (Abner, Oct 18: more space between groups).
 */
function matrixGroupUnits(
  sid: string,
  groups: readonly MatrixGroup[],
  set: [number, number],
  numbers: readonly string[],
): PacketUnit[] {
  const units: PacketUnit[] = []
  let previousMeal: string | null = null
  for (const group of groups) {
    const rows = rowsAtStops(group.rows, set)
    if (rows.length === 0) {
      continue
    }
    const id = `${sid}|${group.mealKey}`
    if (previousMeal !== group.mealKey) {
      units.push({
        id: `${sid}|${group.mealKey}|title`,
        group: id,
        role: 'band',
        kind: 'heading',
        text: group.mealTitle,
      })
      units.push(matrixHead(`${sid}|${group.mealKey}|head`, [], id))
      previousMeal = group.mealKey
    }
    if (
      groups.filter((candidate) => candidate.mealKey === group.mealKey && rowsAtStops(candidate.rows, set).length > 0)
        .length > 1
    ) {
      units.push({
        id: `${id}|${group.text}|band`,
        group: id,
        role: 'band',
        kind: 'band',
        text: group.text,
        note: null,
        level: 2,
      })
    }
    for (const row of rows) {
      units.push(matrixRowUnit(id, row, set, numbers))
    }
    if (group.subtotal) {
      units.push({
        id: `${id}|subtotal`,
        group: id,
        role: 'tail',
        kind: 'matrix-subtotal',
        label: group.subtotal.label,
        counts: group.subtotal.counts.slice(set[0], set[1]),
        total: group.subtotal.total,
      })
    }
  }
  return units
}

function matrixHead(id: string, stops: readonly PacketStop[], group: string | null = null): PacketUnit {
  return {
    id,
    group,
    role: 'band',
    kind: 'matrix-head',
    numbers: stops.map((stop) => stop.identity.number),
  }
}

/** "1–2", "3–5", "2", "1, 3": the stops a pack row goes to, in route order. */
export function stopRange(numbers: readonly string[]): string {
  const values = [...new Set(numbers.map(Number))].sort((a, b) => a - b)
  if (values.length === 0) {
    return '–'
  }
  // Each run of 2+ consecutive stops as "1–5": "1–5, 11–14", never nine numbers down a narrow column.
  const runs: string[] = []
  let start = values[0]!
  values.forEach((n, i) => {
    const next = values[i + 1]
    if (next !== n + 1) {
      runs.push(start === n ? String(n) : `${start}–${n}`)
      if (next !== undefined) {
        start = next
      }
    }
  })
  return runs.join(', ')
}

/** "Stop 1", "Stops 1 and 2", "Stops 1, 2 and 4", "Stops 3 to 5" (a run of three or more). */
export function stopListWords(numbers: readonly string[]): string {
  if (numbers.length === 1) {
    return `Stop ${numbers[0]}`
  }
  const values = numbers.map(Number)
  const run = values.every((n, i) => i === 0 || n === values[i - 1]! + 1)
  if (run && numbers.length >= 3) {
    return `Stops ${numbers[0]} to ${numbers[numbers.length - 1]}`
  }
  return `Stops ${numbers.slice(0, -1).join(', ')} and ${numbers[numbers.length - 1]}`
}

/**
 * The line under the title says what the page is for, briefly (Abner, Oct 20: "replace the legend with a short
 * description of what the page represents"); a day printed in column sets says which stops this set holds.
 */
export function productionSummaryNote(set: readonly PacketStop[], stopCount: number): string {
  return set.length < stopCount ? `${stopSpan(set)} of ${stopCount}; Total counts every stop.` : ''
}

export function productionSummarySectionId(dayIso: string, part = 1): string {
  return part === 1 ? `${dayIso}|production` : `${dayIso}|production|part-${part}`
}

/**
 * The production summary & pack list for one delivery day: a section per
 * column set of stops, each with its milk table, its production table and its
 * "Stop staged" row. The production table's column head repeats atop the
 * pages it runs onto. None when the day has neither milk nor anything to make.
 */
export function productionSummarySections(day: PacketDay, pullSectionId: string | null = null): PacketSection[] {
  const groups = productionGroups(day)
  const sets: Array<[number, number]> = [[0, day.stops.length]]
  return sets.flatMap((set, k) => {
    const sid = productionSummarySectionId(day.iso, k + 1)
    const stops = day.stops.slice(set[0], set[1])
    const body = matrixGroupUnits(
      `${sid}|production`,
      groups,
      set,
      day.stops.map((stop) => stop.identity.number),
    )
    if (body.length === 0) {
      return []
    }
    const production: PacketUnit[] =
      body.length > 0
        ? [
            {
              id: `${sid}|production|heading`,
              group: null,
              role: 'band',
              kind: 'heading',
              text: 'Production',
            },
            ...body,
          ]
        : []
    const units: PacketUnit[] = [
      ...production,
      // The mockup's last line: what the table leaves out and where it is counted.
      ...(pullSectionId
        ? [
            {
              id: `${sid}|pull-note`,
              group: null,
              role: 'block',
              kind: 'section-note',
              text: 'Milk and other ready-made items are counted on the pull & pack list',
              sectionIds: [pullSectionId],
            } satisfies PacketUnit,
          ]
        : []),
      {
        id: `${sid}|staged`,
        group: null,
        role: 'block',
        kind: 'matrix-staged',
        numbers: stops.map((stop) => stop.identity.number),
      },
    ]
    return [
      {
        id: sid,
        kind: 'production-summary' as const,
        dayIso: day.iso,
        band: {
          name: 'Orders',
          counter: null,
          right: KITCHEN_COPY,
          style: 'solid' as const,
        },
        // Abner, Oct 20: "keep just Production Summary as the page's title. Remove that "Item by stop" overhead".
        title: {
          eyebrow: 'ALL STOPS COMBINED · BY MEALTIME',
          title: 'Production Totals',
          subtitle: productionSummaryNote(stops, day.stops.length),
          note: null,
          dateBox: day.dateBox,
        },
        footer: { key: `${day.iso}|production`, label: 'Production Totals', counted: false },
        units,
        repeat: [],
        preferOnePage: false,
      },
    ]
  })
}
